import { FC, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AssessmentMe, assessmentsApi } from 'src/api/assessments';
import { MuiModal } from 'src/components/material/MuiModal';

import { AssessmentForm, AssessmentFormValues } from './AssessmentForm';
import {
  SUBMITTED_TEXT,
  SUBMIT_FAILED_TEXT,
  nextWindow,
} from './assessmentText';

/** Remembered per Circle for the browser session when the member chooses "Svar senere". */
export const laterStorageKey = (circleId: string): string =>
  `assessment:later:${circleId}`;

const wasPutOff = (circleId: string): boolean => {
  try {
    return window.sessionStorage.getItem(laterStorageKey(circleId)) === '1';
  } catch {
    return false;
  }
};

const rememberPutOff = (circleId: string): void => {
  try {
    window.sessionStorage.setItem(laterStorageKey(circleId), '1');
  } catch {
    // Private mode or blocked storage: the questions simply come back next time.
  }
};

interface AssessmentDialogProps {
  circleId: string;
  /** Called after a successful answer, so the Måling tab can refresh. */
  onAnswered?: () => void;
}

/**
 * The "before" questions, asked over the Circle page the first time a member
 * opens a LONELINESS Circle (the answer is worth nothing once the programme
 * has started). Members only: the API says who may answer. "Svar senere"
 * closes it for the browser session; the Måling tab asks again.
 */
export const AssessmentDialog: FC<AssessmentDialogProps> = ({
  circleId,
  onAnswered,
}) => {
  const [me, setMe] = useState<AssessmentMe | null>(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current || wasPutOff(circleId)) {
      return undefined;
    }
    requested.current = true;
    let cancelled = false;
    assessmentsApi
      .getMe(circleId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setMe(result);
        setOpen(result.can_answer && nextWindow(result.responses) === 'BEFORE');
      })
      .catch(() => {
        // Not a member in good standing, or the API is away: no dialog.
      });
    return () => {
      cancelled = true;
    };
  }, [circleId]);

  const handleLater = () => {
    rememberPutOff(circleId);
    setOpen(false);
  };

  const handleSubmit = async (values: AssessmentFormValues) => {
    setSubmitting(true);
    try {
      await assessmentsApi.submit(circleId, {
        window: 'BEFORE',
        answers: values.answers,
        consent_service: true,
        consent_research: values.consentResearch,
      });
      toast.success(SUBMITTED_TEXT);
      setOpen(false);
      onAnswered?.();
    } catch {
      toast.error(SUBMIT_FAILED_TEXT);
    } finally {
      setSubmitting(false);
    }
  };

  if (!me || !open) {
    return null;
  }

  return (
    <MuiModal open={open} setOpen={handleLater} width={680}>
      <AssessmentForm
        instrument={me.instrument}
        window="BEFORE"
        submitting={submitting}
        onSubmit={handleSubmit}
        onLater={handleLater}
      />
    </MuiModal>
  );
};
